import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, BookOpen, Building2, Lightbulb, MessageCircle, Newspaper, Ticket } from 'lucide-react';
import { gefundenText, sichtbareGruppen, titelFilter, vergleichsForm, type RahmenEintrag, type RahmenGruppe } from '../SeitenRahmen';
import type { HilfeUebersicht } from './hilfeApi';
import { anfragenDer, HILFE_TEXTE, organisationGruppe } from './hilfeTexte';

// Die Leiste der Seite als Tabelle (BAU-HILFE.md 1.1, 1.2, 9.2 Punkt 1; PF-308-1), dazu Adressen, Suche und die Umleitung
// alter Adressen; reine Funktionen ohne JSX. Jede Unterseite (PF-309 bis PF-323) trägt eine Zeile in HILFE_LEISTE und ihre Route
// in HilfeSeite.tsx ein; Reihenfolge, Gruppen, offener Eintrag und Suche ergeben sich daraus.

export const HILFE_PFAD = '/hilfe';
export type HilfeBereich = 'dokumente' | 'chat' | 'neuigkeiten' | 'tickets' | 'stoerungen' | 'wuensche' | 'organisation';
/** Reihenfolge der Leiste (BAU-HILFE 1.1, 9.2 Punkt 1): Gruppe ohne Titel, dann „Meine Anfragen“, dann „{Organisation}“. */
export const HILFE_REIHENFOLGE: HilfeBereich[] = ['dokumente', 'chat', 'neuigkeiten', 'tickets', 'stoerungen', 'wuensche', 'organisation'];
/** organisation = brand.tenantLabel; uebersicht null, solange sie lädt oder fehlt. */
export type LeistenKontext = { uebersicht: HilfeUebersicht | null; organisation: string };
export type HilfeLeistenZeile = {
  bereich: HilfeBereich;
  gruppe: 'allgemein' | 'anfragen' | 'organisation';
  titel: (k: LeistenKontext) => string;
  symbol: LucideIcon;
  /** Ohne Übersicht (uebersicht null) nur true, wenn der Inhalt aus dem Frontend kommt (heute nur „Dokumente“). */
  sichtbar: (k: LeistenKontext) => boolean;
  /** Sichtbare Zahl hinter dem Wort, nur > 0. */
  zahl?: (k: LeistenKontext) => number | undefined;
  /** Vorlesetext, wenn ein Punkt steht. */
  punkt?: (k: LeistenKontext) => string | undefined;
  stichwoerter?: string[];
};

export const HILFE_LEISTE: HilfeLeistenZeile[] = [
  { bereich: 'dokumente', gruppe: 'allgemein', titel: () => HILFE_TEXTE.leiste.dokumente, symbol: BookOpen, sichtbar: () => true },
  // PF-309: nur, wenn der Server den Assistenten für dieses Konto anbietet (F21-3 Schalter, F21-4 Deckel).
  { bereich: 'chat', gruppe: 'allgemein', titel: () => HILFE_TEXTE.leiste.chat, symbol: MessageCircle, sichtbar: k => k.uebersicht?.assistent === true },
  // PF-323: für alle, sobald die Übersicht da ist; Punkt bei ungelesenen Neuigkeiten (BAU-HILFE 9.2 Punkt 1).
  { bereich: 'neuigkeiten', gruppe: 'allgemein', titel: () => HILFE_TEXTE.leiste.neuigkeiten, symbol: Newspaper, sichtbar: k => k.uebersicht !== null,
    punkt: k => ((k.uebersicht?.zaehler.neuigkeiten ?? 0) > 0 ? HILFE_TEXTE.leiste.ungeleseneNeuigkeiten : undefined) },
  // PF-310-1: für alle, sobald die Übersicht da ist; Zahl offener Anfragen, Punkt bei einer ungelesenen Antwort.
  { bereich: 'tickets', gruppe: 'anfragen', titel: () => HILFE_TEXTE.leiste.tickets, symbol: Ticket, sichtbar: k => k.uebersicht !== null,
    zahl: k => k.uebersicht?.zaehler.offeneTickets, punkt: k => ((k.uebersicht?.zaehler.ungelesen ?? 0) > 0 ? HILFE_TEXTE.leiste.neueAntwort : undefined) },
  // PF-311-1: wie „Tickets“, mit der Zahl aktiver Störungen. TriangleAlert heißt in lucide-react 0.300 noch AlertTriangle.
  { bereich: 'stoerungen', gruppe: 'anfragen', titel: () => HILFE_TEXTE.leiste.stoerungen, symbol: AlertTriangle, sichtbar: k => k.uebersicht !== null,
    zahl: k => k.uebersicht?.zaehler.aktiveStoerungen },
  // PF-311-2: ohne Zahl (BAU-HILFE 1.1).
  { bereich: 'wuensche', gruppe: 'anfragen', titel: () => HILFE_TEXTE.leiste.wuensche, symbol: Lightbulb, sichtbar: k => k.uebersicht !== null },
  // PF-312: Gruppe „{Organisation}“ nur mit dem Leserecht der eigenen Firma (uebersicht.firma, members.read).
  { bereich: 'organisation', gruppe: 'organisation', titel: k => anfragenDer(k.organisation), symbol: Building2, sichtbar: k => k.uebersicht?.firma === true },
];

const GRUPPEN: { key: HilfeLeistenZeile['gruppe']; titel: (k: LeistenKontext) => string | null }[] = [
  { key: 'allgemein', titel: () => null },
  { key: 'anfragen', titel: () => HILFE_TEXTE.leiste.meineAnfragen },
  { key: 'organisation', titel: k => organisationGruppe(k.organisation) },
];

/** '/hilfe/<bereich>' bzw. '/hilfe/<bereich>/<rest>'. */
export const hilfeAdresse = (bereich: HilfeBereich, rest?: string) => (rest ? `${HILFE_PFAD}/${bereich}/${rest}` : `${HILFE_PFAD}/${bereich}`);
export const hilfeBereichGebaut = (bereich: HilfeBereich) => HILFE_LEISTE.some(zeile => zeile.bereich === bereich);
export function hilfeEintragSichtbar(bereich: HilfeBereich, k: LeistenKontext): boolean {
  const zeile = HILFE_LEISTE.find(eintrag => eintrag.bereich === bereich);
  return !!zeile && zeile.sichtbar(k);
}

/** Offen ist ein Eintrag, wenn der Pfad mit seiner Adresse beginnt (auch ein Detail darunter). */
const beginntMit = (pfad: string, adresse: string) => pfad === adresse || pfad.startsWith(`${adresse}/`);

/** Die Gruppen der Leiste nach HILFE_REIHENFOLGE, mit aktiv; unsichtbare Einträge und leere Gruppen entfallen. */
export function hilfeGruppen(k: LeistenKontext, pfad: string): RahmenGruppe[] {
  const zeilen = [...HILFE_LEISTE].sort((a, b) => HILFE_REIHENFOLGE.indexOf(a.bereich) - HILFE_REIHENFOLGE.indexOf(b.bereich));
  return sichtbareGruppen(GRUPPEN.map(gruppe => ({
    key: gruppe.key,
    titel: gruppe.titel(k),
    eintraege: zeilen.filter(zeile => zeile.gruppe === gruppe.key).map((zeile): RahmenEintrag => {
      const to = hilfeAdresse(zeile.bereich);
      return { key: zeile.bereich, titel: zeile.titel(k), to, symbol: zeile.symbol, aktiv: beginntMit(pfad, to), sichtbar: zeile.sichtbar(k), stichwoerter: zeile.stichwoerter };
    }),
  })));
}

/** Anker eines Hilfe-Themas, wie hilfeAnker in pages/einstellungen/Hilfe.tsx. */
const themenAnker = (id: string) => `hilfe-${id}`;
/** Anker eines eigenen Dokuments des Betreibers (PF-323, BAU-HILFE 9.2 Punkt 3). */
export const dokumentAnker = (id: string) => `hilfe-b-${id}`;

/** F-23 (PF-NB-hilfe-1, Z-01): passt der Suchtext? Ganz enthalten in einem der Teile, oder er hat mehrere Wörter und jedes Wort ab
 *  drei Zeichen steht in einem Teil („gute Rechtsfrage stellen“ findet „Wie stelle ich eine gute Rechtsfrage?“ mit dem Stichwort
 *  „Rechtsfrage stellen“). Ohne Suchtext passt alles. */
export function passtZuTeilen(teile: readonly string[], text: string): boolean {
  const muster = vergleichsForm(text.trim());
  if (!muster) return true;
  const formen = teile.map(teil => vergleichsForm(teil));
  if (formen.some(form => form.includes(muster))) return true;
  const woerter = muster.split(/\s+/).filter(wort => wort.length >= 3);
  return woerter.length > 1 && woerter.every(wort => formen.some(form => form.includes(wort)));
}

/** Suche der Leiste (BAU-HILFE 1.1): Einträge über titelFilter, dazu die Fragen der Hilfe-Themen und (PF-323) die Titel der
 *  eigenen Dokumente, solange diese geladen sind. Trifft eine Frage oder ein Titel, steht „Dokumente“ im Ergebnis mit dem Fund und
 *  dem Ziel der ersten passenden Frage, sonst des ersten passenden Dokuments. F-23: auch über die Stichwörter des Themas und Wort
 *  für Wort (passtZuTeilen). */
export function hilfeSuche(gruppen: RahmenGruppe[], themen: { id: string; frage: string; stichwoerter?: readonly string[] }[], text: string,
  eigene: { id: string; titel: string }[] = []): RahmenGruppe[] {
  const treffer = titelFilter(gruppen, text);
  const muster = vergleichsForm(text.trim());
  const frage = muster ? themen.find(thema => passtZuTeilen([thema.frage, ...(thema.stichwoerter ?? [])], text)) : undefined;
  const dokument = muster && !frage ? eigene.find(eintrag => vergleichsForm(eintrag.titel).includes(muster)) : undefined;
  const anker = frage ? themenAnker(frage.id) : dokument ? dokumentAnker(dokument.id) : null;
  if (!anker) return treffer;
  const dokumente = hilfeAdresse('dokumente');
  return gruppen.map(gruppe => {
    const gefunden = treffer.find(t => t.key === gruppe.key)?.eintraege ?? [];
    return { ...gruppe, eintraege: gruppe.eintraege.flatMap(eintrag => {
      const ueberTitel = gefunden.find(g => g.key === eintrag.key);
      if (ueberTitel) return [ueberTitel];
      return eintrag.to === dokumente ? [{ ...eintrag, to: `${dokumente}#${anker}`, fund: gefundenText(text.trim()) }] : [];
    }) };
  }).filter(gruppe => gruppe.eintraege.length > 0);
}

/** Suche „Anleitungen durchsuchen“ auf „Dokumente“ (BAU-HILFE 1.3, 9.2 Punkt 3): eingebaute Themen über Frage, Schritte und
 *  Hinweise, eigene Dokumente über Titel, Text und Stichwörter, beide mit vergleichsForm; ohne Suchtext alles. */
export function anleitungenFiltern<T extends { frage: string; schritte: string[]; hinweise: string[]; stichwoerter?: readonly string[] }, D extends { titel: string; text: string; stichwoerter: string[] }>(
  themen: T[], eigene: D[], text: string): { themen: T[]; eigene: D[] } {
  const muster = vergleichsForm(text.trim());
  if (!muster) return { themen, eigene };
  const passt = (teile: string[]) => passtZuTeilen(teile, text);
  return { themen: themen.filter(thema => passt([thema.frage, ...(thema.stichwoerter ?? []), ...thema.schritte, ...thema.hinweise])),
    eigene: eigene.filter(dokument => passt([dokument.titel, dokument.text, ...dokument.stichwoerter])) };
}

// Alte Verweise ?hilfe=<wert> (BAU-HILFE 1.2, Tabelle wörtlich). Eine Kennung im zweiten Parameter führt ins Detail, wenn sie
// gültig ist; sonst bleibt es bei der Liste.
const TICKET = /^SUP-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{7}$/;
const WUNSCH = /^WUN-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{5}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type Umleitung = { bereich: HilfeBereich; kennung?: { parameter: string; muster: RegExp } };
const UMLEITUNG = new Map<string, Umleitung>([
  ['dokumente', { bereich: 'dokumente' }],
  ['chat', { bereich: 'chat' }],
  ['assistent', { bereich: 'chat' }],
  ['tickets', { bereich: 'tickets', kennung: { parameter: 'ticket', muster: TICKET } }],
  ['anfragen', { bereich: 'tickets', kennung: { parameter: 'ticket', muster: TICKET } }],
  ['stoerungen', { bereich: 'stoerungen', kennung: { parameter: 'stoerung', muster: UUID } }],
  ['wuensche', { bereich: 'wuensche', kennung: { parameter: 'wunsch', muster: WUNSCH } }],
  ['firma', { bereich: 'organisation' }],
  ['organisation', { bereich: 'organisation' }],
]);
const HILFE_PARAMETER = 'hilfe';

/** ?hilfe=… → Adresse der Seite; ein unbekannter Wert führt zu den Dokumenten; ohne Parameter null. */
export function hilfeZielAusParameter(search: string): string | null {
  const parameter = new URLSearchParams(search);
  const wert = parameter.get(HILFE_PARAMETER);
  if (wert === null) return null;
  const umleitung = UMLEITUNG.get(wert);
  if (!umleitung) return hilfeAdresse('dokumente');
  const kennung = umleitung.kennung ? parameter.get(umleitung.kennung.parameter) : null;
  return kennung && umleitung.kennung?.muster.test(kennung) ? hilfeAdresse(umleitung.bereich, kennung) : hilfeAdresse(umleitung.bereich);
}

/** Die Abfrage ohne ?hilfe= und dessen Kennungen (ticket, stoerung, wunsch): die Herkunft hinter der Seite. */
export function ohneHilfeParameter(search: string): string {
  const parameter = new URLSearchParams(search);
  for (const name of [HILFE_PARAMETER, ...[...UMLEITUNG.values()].flatMap(u => (u.kennung ? [u.kennung.parameter] : []))]) parameter.delete(name);
  const rest = parameter.toString();
  return rest ? `?${rest}` : '';
}

/** /einstellungen/hilfe#hilfe-<id> → /hilfe/dokumente#hilfe-<id> (Kennung nur aus a-z, 0-9 und -), sonst /hilfe/dokumente. */
export const einstellungenHilfeZiel = (hash: string) => (/^#hilfe-[a-z0-9-]+$/.test(hash) ? `${hilfeAdresse('dokumente')}${hash}` : hilfeAdresse('dokumente'));
